import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, timer, BehaviorSubject, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface SystemResourcesResponse {
  cpuUsagePercent: number;
  memoryUsedMb: number;
  memoryTotalMb: number;
  diskUsedGb: number;
  diskTotalGb: number;
  threadCount: number;
  uptimeSeconds: number;
}

export interface ExternalServiceStatus {
  name: string;
  isReachable: boolean;
  responseTimeMs?: number;
  lastChecked: string;
  error?: string;
}

export interface ExternalServicesResponse {
  services: ExternalServiceStatus[];
  allReachable: boolean;
}

export interface ComprehensiveHealthResponse {
  overall: string;
  timestamp: string;
  components: Record<string, { status: string; description?: string }>;
}

// Health response interfaces
export interface SimpleHealthResponse {
  status: string;
  timestamp: string;
  message: string;
  version: string;
}

export interface FullHealthResponse {
  status: string;
  timestamp: string;
  version: string;
  environment: string;
  database: string;
  deploymentMethod: string;
}

export interface DatabaseHealthResponse {
  status: string;
  timestamp: string;
  statistics: {
    userCount: number;
    orderCount: number;
    parcelCount: number;
  };
  message: string;
}

export interface VersionResponse {
  application: string;
  version: string;
  framework: string;
  environment: string;
  timestamp: string;
  machineName: string;
  processId: number;
  deploymentInfo: {
    method: string;
    autoDeploy: boolean;
  };
}

export interface HealthStatus {
  isHealthy: boolean;
  responseTime: number;
  lastChecked: Date;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class HealthService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;

  // Auto-refresh subjects
  private autoRefreshEnabled = new BehaviorSubject<boolean>(false);
  private refreshInterval = new BehaviorSubject<number>(30000); // 30 seconds default

  // Simple health check
  getSimpleHealth(): Observable<{ data: SimpleHealthResponse; status: HealthStatus }> {
    const startTime = Date.now();
    
    return this.http.get<SimpleHealthResponse>(`${this.apiUrl}/health/simple`).pipe(
      map(data => ({
        data,
        status: {
          isHealthy: true,
          responseTime: Date.now() - startTime,
          lastChecked: new Date()
        }
      })),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => ({
          data: null,
          status: {
            isHealthy: false,
            responseTime: Date.now() - startTime,
            lastChecked: new Date(),
            error: this.getErrorMessage(error)
          }
        }));
      })
    );
  }

  // Full health check
  getFullHealth(): Observable<{ data: FullHealthResponse; status: HealthStatus }> {
    const startTime = Date.now();
    
    return this.http.get<FullHealthResponse>(`${this.apiUrl}/health`).pipe(
      map(data => ({
        data,
        status: {
          isHealthy: data.status === 'Healthy',
          responseTime: Date.now() - startTime,
          lastChecked: new Date()
        }
      })),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => ({
          data: null,
          status: {
            isHealthy: false,
            responseTime: Date.now() - startTime,
            lastChecked: new Date(),
            error: this.getErrorMessage(error)
          }
        }));
      })
    );
  }

  // Database health check
  getDatabaseHealth(): Observable<{ data: DatabaseHealthResponse; status: HealthStatus }> {
    const startTime = Date.now();
    
    return this.http.get<DatabaseHealthResponse>(`${this.apiUrl}/health/database`).pipe(
      map(data => ({
        data,
        status: {
          isHealthy: data.status === 'Healthy',
          responseTime: Date.now() - startTime,
          lastChecked: new Date()
        }
      })),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => ({
          data: null,
          status: {
            isHealthy: false,
            responseTime: Date.now() - startTime,
            lastChecked: new Date(),
            error: this.getErrorMessage(error)
          }
        }));
      })
    );
  }

  // Version information
  getVersion(): Observable<{ data: VersionResponse; status: HealthStatus }> {
    const startTime = Date.now();
    
    return this.http.get<VersionResponse>(`${this.apiUrl}/health/version`).pipe(
      map(data => ({
        data,
        status: {
          isHealthy: true,
          responseTime: Date.now() - startTime,
          lastChecked: new Date()
        }
      })),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => ({
          data: null,
          status: {
            isHealthy: false,
            responseTime: Date.now() - startTime,
            lastChecked: new Date(),
            error: this.getErrorMessage(error)
          }
        }));
      })
    );
  }

  // Auto-refresh functionality
  getAutoRefreshHealth() {
    return this.autoRefreshEnabled.pipe(
      switchMap(enabled => {
        if (enabled) {
          return timer(0, this.refreshInterval.value).pipe(
            switchMap(() => this.getFullHealth())
          );
        }
        return this.getFullHealth();
      })
    );
  }

  // Auto-refresh controls
  enableAutoRefresh(intervalMs: number = 30000) {
    this.refreshInterval.next(intervalMs);
    this.autoRefreshEnabled.next(true);
  }

  disableAutoRefresh() {
    this.autoRefreshEnabled.next(false);
  }

  setRefreshInterval(intervalMs: number) {
    this.refreshInterval.next(intervalMs);
  }

  // Comprehensive health check (all endpoints)
  getAllHealthChecks(): Observable<{
    simple: { data: SimpleHealthResponse; status: HealthStatus } | null;
    full: { data: FullHealthResponse; status: HealthStatus } | null;
    database: { data: DatabaseHealthResponse; status: HealthStatus } | null;
    version: { data: VersionResponse; status: HealthStatus } | null;
    overallStatus: 'healthy' | 'degraded' | 'unhealthy';
  }> {
    return forkJoin({
      simple: this.getSimpleHealth().pipe(catchError(err => of(err as { data: SimpleHealthResponse; status: HealthStatus } | null))),
      full: this.getFullHealth().pipe(catchError(err => of(err as { data: FullHealthResponse; status: HealthStatus } | null))),
      database: this.getDatabaseHealth().pipe(catchError(err => of(err as { data: DatabaseHealthResponse; status: HealthStatus } | null))),
      version: this.getVersion().pipe(catchError(err => of(err as { data: VersionResponse; status: HealthStatus } | null))),
    }).pipe(
      map(({ simple, full, database, version }) => {
        const healthyChecks = [
          simple?.status.isHealthy,
          full?.status.isHealthy,
          database?.status.isHealthy,
          version?.status.isHealthy,
        ].filter(Boolean).length;

        const overallStatus: 'healthy' | 'degraded' | 'unhealthy' =
          healthyChecks === 4 ? 'healthy' : healthyChecks >= 2 ? 'degraded' : 'unhealthy';

        return { simple, full, database, version, overallStatus };
      })
    );
  }

  // System resources (CPU, memory, disk, threads)
  getSystemResources(): Observable<SystemResourcesResponse> {
    return this.http
      .get<SystemResourcesResponse>(`${this.apiUrl}/health/system-resources`)
      .pipe(catchError(() => throwError(() => new Error('System resources unavailable'))));
  }

  // External service reachability (Shopee API, internet)
  getExternalServices(): Observable<ExternalServicesResponse> {
    return this.http
      .get<ExternalServicesResponse>(`${this.apiUrl}/health/external-services`)
      .pipe(catchError(() => throwError(() => new Error('External services check unavailable'))));
  }

  // Comprehensive ASP.NET health checks endpoint
  getComprehensive(): Observable<ComprehensiveHealthResponse> {
    return this.http
      .get<ComprehensiveHealthResponse>(`${this.apiUrl}/health/comprehensive`)
      .pipe(catchError(() => throwError(() => new Error('Comprehensive health check unavailable'))));
  }

  private getErrorMessage(error: HttpErrorResponse): string {
    if (error.error instanceof ErrorEvent) {
      return `Network error: ${error.error.message}`;
    }
    
    switch (error.status) {
      case 0:
        return 'Unable to connect to server';
      case 503:
        return 'Service unavailable';
      case 500:
        return 'Internal server error';
      default:
        return `HTTP ${error.status}: ${error.message}`;
    }
  }
}
